/*
 * Purpose: AutoBlocks connector library panel — native connectors for this plan
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plug, Sparkles } from 'lucide-react';
import type { OutcomePlan } from '@/services/autoblocks/types';
import { useConnectorLibraryStore } from '@/services/connectors/library/store';
import { PRODUCT_BRAND } from '@/lib/productTerms';

interface ConnectorLibraryPanelProps {
  plan: OutcomePlan;
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  ready: 'default',
  needs_credentials: 'destructive',
  needs_connect: 'secondary',
  scaffold: 'outline',
};

export function ConnectorLibraryPanel({ plan }: ConnectorLibraryPanelProps) {
  const connectors = plan.resolvedConnectors ?? [];
  const built = plan.builtConnectors ?? [];

  if (!connectors.length && !built.length) return null;

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Plug className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">{PRODUCT_BRAND} connectors</CardTitle>
          <Badge variant="outline" className="text-[10px]">
            Owned in-house
          </Badge>
        </div>
        <CardDescription className="text-xs">
          Native integrations {PRODUCT_BRAND} runs — no third-party automation tools required.
          {plan.connectorFactoryNote && (
            <span className="block mt-1 text-muted-foreground">{plan.connectorFactoryNote}</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {built.length > 0 && (
          <div className="rounded-md bg-primary/5 border border-primary/20 p-3 text-xs space-y-2">
            <p className="font-medium flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" />
              AI built {built.length} new connector{built.length > 1 ? 's' : ''} for this outcome
            </p>
            <ul className="space-y-1 text-muted-foreground">
              {built.map((c) => (
                <li key={c.id}>
                  <code className="text-[10px]">{c.id}</code> — {c.name}: {c.description}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="space-y-2">
          {connectors.map((c) => {
            const def = useConnectorLibraryStore.getState().getById(c.connectorId);
            return (
              <li key={c.connectorId} className="rounded-md border p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-medium">{c.name}</span>
                  <Badge variant={STATUS_VARIANT[c.status] ?? 'outline'} className="text-[10px]">
                    {c.status.replace(/_/g, ' ')}
                  </Badge>
                  {c.isNewlyBuilt && (
                    <Badge variant="secondary" className="text-[10px]">
                      AI-built
                    </Badge>
                  )}
                  {def && (
                    <Badge variant="outline" className="text-[10px]">
                      {def.runtime}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2">{c.purpose}</p>
                <ol className="text-xs text-muted-foreground list-decimal list-inside space-y-0.5">
                  {c.setupSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
